import FileSaver from 'file-saver';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';
import { replace } from 'connected-react-router';

import { TESTS_PAGE_STATUSES, FILTER_TYPE_PARAM_NAME, TEST_LIST_CLOUD_KEY, TEST_OVERVIEW_PATH } from '../../../constants';
import { OVERVIEW_TABS } from '../../../ducks/common/test-overview/constants';
import { getPathWithTestStatus } from './utils';

export const createTestListOperations = ({
  actions,
  selectors,
  operations,
  filterOperations,
  filterActions,
  statusStoreKey,
  type,
  pathPattern = TEST_OVERVIEW_PATH,
  cloudTypeKey = TEST_LIST_CLOUD_KEY,
}) => ({

  getTestCases: (options) => (dispatch, getState) => {
    const payload = selectors.statusSelector(options);
    const params = selectors.testCasesParamsSelector(getState(), payload);
    return dispatch(actions.getTestCases(params, payload));
  },

  updateRouteParams: (statusKey = TESTS_PAGE_STATUSES.all.key) => (dispatch, getState) => {
    const { search } = selectors.routerLocation(getState());

    return dispatch(replace({
      pathname: getPathWithTestStatus({
        pathPattern,
        tabName: OVERVIEW_TABS.tests.key,
        statusKey,
        cloudTypeKey,
      }),
      search,
    }));
  },

  getTestNamesByFilter: (key) => async (dispatch) => {
    const { status } = TESTS_PAGE_STATUSES[key];
    await dispatch(actions.resetPage());
    window.localStorage.setItem(statusStoreKey, key);
    return Promise.all([
      dispatch(operations.updateRouteParams(key)),
      dispatch(operations.getTestCases({ type: status })),
    ]);
  },

  changePage: (payload) => async (dispatch) => {
    await dispatch(actions.changePage(payload));
    return dispatch(operations.getTestCases());
  },

  changeSize: (payload) => async (dispatch) => {
    await Promise.all([
      dispatch(actions.changeSize(payload)),
      dispatch(actions.resetPage()),
    ]);
    return dispatch(operations.getTestCases());
  },

  changeSorting: (payload) => (dispatch) => {
    dispatch(actions.changeSorting(payload));
    return dispatch(operations.getTestCases());
  },

  changeTrendsInterval: (payload) => (dispatch) => {
    dispatch(actions.changeTrendsInterval(payload));
    return dispatch(operations.getTestCasesTrends());
  },

  changeSelectedBar: (payload) => (dispatch) => {
    dispatch(actions.changeSelectedBar(payload));
    return dispatch(operations.getTestCases());
  },

  refreshTestCases: () => (dispatch) => {
    dispatch(actions.resetPage());
    return Promise.all([
      dispatch(operations.getTestCases()),
      dispatch(operations.getTestCasesTrends()),
    ]);
  },

  getTestCasesCSV: (key) => (dispatch, getState) => {
    const { status } = TESTS_PAGE_STATUSES[key];
    const payload = selectors.statusSelector({ type: status });
    const queryParams = selectors.testCasesParamsSelector(getState(), payload);
    const requestOptions = {
      params: {
        responseType: 'blob',
      },
      queryParams,
    };
    return dispatch(actions.getTestCasesCSV(requestOptions))
      .then((response) => {
        const fileData = [response.data];
        FileSaver.saveAs(new Blob(fileData), `test-cases-${type}-${new Date().getTime()}.csv`);
      }).catch((error) => (
        dispatch(notificationActions.notifyDanger(
          error.message, 'We\'ve experienced an issue while downloading CSV file.', true,
        ))
      ));
  },

  getTestCasesTrends: () => (dispatch, getState) => {
    const params = selectors.testCasesTrendsParamsSelector(getState());
    return dispatch(actions.getTestCasesTrends(params));
  },

  cleanup: () => (dispatch) => (
    Promise.all([
      dispatch(actions.cleanup()),
      dispatch(filterActions.cleanup()),
    ])
  ),

  initialize: (options) => (dispatch) => {
    const urlStatus = options?.[FILTER_TYPE_PARAM_NAME];
    const storedKey = window.localStorage.getItem(statusStoreKey) || TESTS_PAGE_STATUSES.all.key;
    const shouldChangeStatus = !!urlStatus && urlStatus !== storedKey;
    const status = shouldChangeStatus ? urlStatus : storedKey;
    const shouldUpdateRoute = urlStatus !== status;

    const params = {
      [FILTER_TYPE_PARAM_NAME]: status,
    };

    if (shouldChangeStatus) {
      window.localStorage.setItem(statusStoreKey, status);
    }

    if (shouldUpdateRoute) {
      dispatch(operations.updateRouteParams(status));
    }

    return dispatch(filterOperations.initialize()).then(() => (
      Promise.all([
        dispatch(operations.getTestCases(params)),
        dispatch(operations.getTestCasesTrends(params)),
      ])
    )).catch((error) => {
      dispatch(notificationActions.notifyDanger(
        error.message, 'We\'ve experienced an issue loading test data.', true,
      ));
    });
  },
});
